import { ProcessedBidResponse } from 'ts-types';
import { makePostRequest } from '../utils';
import { ANALYTICS_BID_URL } from '../constants';
import {
  findAuctionIdsFromAd,
  processBidResponses as processGetBidResponses,
  BidResponses,
} from './prebid';

type Bid = {
  ad: string;
  creativeId: number;
  appnexus?: { buyerMemberId: number };
  cpm: number;
  bidderCode: string;
  auctionId: string;
};

type AuctionEndEvent = {
  bidsReceived?: Bid[];
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const processBidResponse = (bid: Bid): ProcessedBidResponse | null => {
  if (bid.bidderCode !== 'appnexus') return null;
  const buyerAuctionIds = findAuctionIdsFromAd(bid.ad);
  const { auctionId, cpm, creativeId, appnexus } = bid;
  return {
    cpm,
    auctionId,
    creativeId,
    buyerAuctionIds: buyerAuctionIds.filter(
      (v, i, a) => a.indexOf(v) === i && v !== undefined
    ),
    buyerMemberId: appnexus ? appnexus.buyerMemberId : 0,
  };
};

const makeAuctionEndEventHandler = (apiKey: string, loadId: string) => async (
  auctionEndEvent: AuctionEndEvent
) => {
  if (
    !auctionEndEvent.bidsReceived ||
    auctionEndEvent.bidsReceived.length === 0
  ) {
    return;
  }

  const processedBidResponses = auctionEndEvent.bidsReceived
    .map((item) => processBidResponse(item))
    .filter((item) => item !== null && item !== undefined);

  await makePostRequest(
    ANALYTICS_BID_URL,
    JSON.stringify(processedBidResponses),
    apiKey,
    loadId
  );
};

const MAX_RETRIES = 100;
const BASE_RETRY_AWAIT_MS = 10;

export const registerBidResponseEventCallback = async (
  apiKey: string,
  loadId: string,
  retries = MAX_RETRIES
): Promise<void> => {
  try {
    // First we capture auctions already ran, and POST them to our endpoint.
    const responses: BidResponses = (<any>window).pbjs.getBidResponses();
    const processedBidResponses = processGetBidResponses(responses);
    if (processedBidResponses.length > 0) {
      await makePostRequest(
        ANALYTICS_BID_URL,
        JSON.stringify(processedBidResponses),
        apiKey,
        loadId
      );
    }

    const eventHandler = makeAuctionEndEventHandler(apiKey, loadId);

    const AUCTION_END = 'auctionEnd';
    (<any>window).pbjs.onEvent(AUCTION_END, eventHandler);
  } catch (e) {
    // PBJS can take some time to load, so we retry.
    if (retries > 1) {
      await sleep(BASE_RETRY_AWAIT_MS * (MAX_RETRIES + 1 - retries));
      await registerBidResponseEventCallback(apiKey, loadId, retries - 1);
    }
  }
};
